/**
 * SanctaMarkdown — lightweight inline markdown renderer for Sancta replies.
 *
 * Handles the four constructs the model actually uses today:
 *   **bold**         → <strong>bold</strong>
 *   *italic*         → <em>italic</em>
 *   `code`           → <code>code</code>
 *   [text](https://) → <a href> new-tab link
 *
 * Everything else falls through as plain text with line breaks preserved
 * (parent bubble has `whitespace-pre-wrap`).
 *
 * Deliberately DOES NOT pull in a full markdown library — the message
 * bodies are short and any heavier renderer would double the widget's
 * JS budget. If future features need lists/tables we'll swap this out.
 */
import React from "react";

// Order matters: bold must be tested BEFORE italic because ** contains *.
type Token = { type: "text" | "bold" | "italic" | "code" | "link"; content: string; href?: string };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  const re = /(\*\*([^*\n]+?)\*\*)|(\*([^*\n]+?)\*)|(`([^`\n]+?)`)|(\[([^\]]+?)\]\((https?:\/\/[^\s)]+)\))/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ type: "text", content: input.slice(lastIndex, m.index) });
    }
    if (m[1]) {
      tokens.push({ type: "bold", content: m[2] });
    } else if (m[3]) {
      tokens.push({ type: "italic", content: m[4] });
    } else if (m[5]) {
      tokens.push({ type: "code", content: m[6] });
    } else if (m[7]) {
      tokens.push({ type: "link", content: m[8], href: m[9] });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < input.length) {
    tokens.push({ type: "text", content: input.slice(lastIndex) });
  }
  return tokens;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode {
  const tokens = tokenize(text || "");
  return tokens.map((t, i) => {
    const key = `${keyPrefix}-${i}`;
    if (t.type === "bold")   return <strong key={key} className="font-semibold">{t.content}</strong>;
    if (t.type === "italic") return <em key={key} className="italic">{t.content}</em>;
    if (t.type === "code")   return <code key={key} className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 font-mono text-[0.9em]">{t.content}</code>;
    if (t.type === "link")   return (
      <a
        key={key}
        href={t.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fuchsia-600 dark:text-fuchsia-300 underline underline-offset-2 hover:text-fuchsia-700"
      >
        {t.content}
      </a>
    );
    return <React.Fragment key={key}>{t.content}</React.Fragment>;
  });
}

const HR_RE = /^\s*([-*_])\1{2,}\s*$/;              // ---, ***, ___
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;               // - item / * item / • item
const NUM_RE = /^\s*(\d+)[.)]\s+(.*)$/;              // 1. item / 2) item
const HEADING_RE = /^\s*(#{1,6})\s+(.*)$/;           // # heading

/**
 * Block-aware renderer: paragraphs, bullet & numbered lists, headings and
 * horizontal rules — with inline markdown inside each line. Keeps the widget
 * lightweight (no external markdown lib).
 */
export const SanctaMarkdown: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const out: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let key = 0;

  const flushList = () => {
    if (!list) return;
    const items = list.items;
    const ordered = list.ordered;
    out.push(
      ordered ? (
        <ol key={`ol-${key++}`} className="list-decimal pl-5 my-1 space-y-0.5">
          {items.map((it, i) => <li key={i}>{renderInline(it, `oli-${i}`)}</li>)}
        </ol>
      ) : (
        <ul key={`ul-${key++}`} className="list-disc pl-5 my-1 space-y-0.5">
          {items.map((it, i) => <li key={i}>{renderInline(it, `uli-${i}`)}</li>)}
        </ul>
      ),
    );
    list = null;
  };

  for (const raw of lines) {
    const line = raw ?? "";
    if (HR_RE.test(line)) {
      flushList();
      out.push(<hr key={`hr-${key++}`} className="my-2 border-black/10 dark:border-white/10" />);
      continue;
    }
    const heading = line.match(HEADING_RE);
    if (heading) {
      flushList();
      out.push(<div key={`h-${key++}`} className="font-bold text-[1.02em] mt-1.5 mb-0.5">{renderInline(heading[2], `h${key}`)}</div>);
      continue;
    }
    const bullet = line.match(BULLET_RE);
    if (bullet) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
      continue;
    }
    const num = line.match(NUM_RE);
    if (num) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(num[2]);
      continue;
    }
    // Plain line (may be blank → spacing handled by whitespace-pre-line).
    flushList();
    if (line.trim() === "") {
      out.push(<div key={`sp-${key++}`} className="h-2" />);
    } else {
      out.push(<div key={`p-${key++}`}>{renderInline(line, `p${key}`)}</div>);
    }
  }
  flushList();

  return <div className={className}>{out}</div>;
};

export default SanctaMarkdown;
